import React, { useState } from 'react';
import { AnchorIcon, ShieldIcon, NetworkIcon, LightbulbIcon, CheckCircleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { AccordionItem } from '../components/AccordionItem';
import { PageHero } from '../components/PageHero';
import { ProcessTimeline } from '../components/ProcessTimeline';
import { Button } from '../components/Button';
interface WoonbotenPageProps {
  onNavigate: (page: string) => void;
}
export function WoonbotenPage({
  onNavigate
}: WoonbotenPageProps) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const faqs = [{
    question: 'Welke banken financieren woonboten?',
    answer: 'Niet elke reguliere bank financiert woonboten, omdat het risicoprofiel anders is dan bij een stenen huis. Wij hebben een specifiek netwerk van gespecialiseerde geldverstrekkers die hier wél ervaring mee hebben. Wij weten precies waar we jouw aanvraag met de grootste kans op succes kunnen indienen.'
  }, {
    question: 'Wat zijn de specifieke eisen voor een woonboothypotheek?',
    answer: 'Er gelden vaak aanvullende eisen. Zo moet de woonboot een vaste ligplaats hebben met een vergunning, ingeschreven staan in het scheepsregister van het Kadaster, en er is vaak een recent taxatierapport van een gespecialiseerde woonboottaxateur nodig. Wij begeleiden je bij al deze specifieke stappen.'
  }, {
    question: 'Hoe verschilt een woonboothypotheek van een reguliere hypotheek?',
    answer: 'De rentetarieven liggen vaak iets hoger door een opslag, en de maximale looptijd of het maximaal te lenen bedrag ten opzichte van de woningwaarde kan afwijken. Ook de notariële afhandeling is specifieker. Omdat wij deze niche door en door kennen, kom je niet voor verrassingen te staan.'
  }, {
    question: 'Wat kost een woonboothypotheek?',
    answer: 'De rentetarieven voor woonboten liggen gemiddeld 0,5% tot 1,5% hoger dan bij een reguliere hypotheek, afhankelijk van de geldverstrekker en het type woonboot. Tijdens een vrijblijvend gesprek bereken ik precies wat de maandlasten in jouw situatie worden.'
  }, {
    question: 'Kan ik een woonboot kopen in Amsterdam?',
    answer: 'Zeker. Amsterdam heeft veel woonboten, maar de ligplaatsvergunningen zijn schaars en waardevol. Ik ken de specifieke regels per stadsdeel en weet welke geldverstrekkers ervaring hebben met Amsterdamse woonboten. Of het nu gaat om de Amstel, de Prinsengracht of de Schinkel — ik help je verder.'
  }, {
    question: 'Heb ik een taxatie nodig voor mijn woonboot?',
    answer: 'Ja, je hebt een taxatierapport nodig van een gespecialiseerde woonboottaxateur. Dit is anders dan een reguliere woningtaxatie. De taxateur beoordeelt onder andere de staat van de romp, de ligplaats en de vergunningssituatie. Ik kan je doorverwijzen naar betrouwbare woonboottaxateurs in de regio.'
  }];
  const timelineSteps = [{
    number: 1,
    title: 'Haalbaarheid',
    description: 'We checken direct de haalbaarheid op basis van de specifieke woonboot, ligplaats en jouw financiële situatie.'
  }, {
    number: 2,
    title: 'Dossier opbouwen',
    description: 'We verzamelen de juiste documenten, zoals een gespecialiseerd taxatierapport en bewijs van inschrijving in het scheepsregister.'
  }, {
    number: 3,
    title: 'Financiering rond',
    description: 'We dienen de aanvraag in bij de juiste bank en begeleiden het traject tot aan de notaris.'
  }];
  return <div className="w-full" data-id="element-750">
      {/* Immersive Hero */}
      <PageHero variant="immersive" title="Dé Specialist in Woonboot Hypotheken" icon={<div className="w-20 h-20 bg-gold/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-gold/50" data-id="element-752">
            <AnchorIcon className="w-10 h-10 text-gold" data-id="element-753" />
          </div>} data-id="element-751" />

      {/* Content Block */}
      <section className="py-20 md:py-28 bg-white" data-id="element-754">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-755">
          <SectionHeading title="Een unieke woning vraagt om unieke kennis" subtitle="Het financieren van een woonboot is een vak apart. Reguliere banken haken vaak af of stellen onmogelijke eisen. Ik heb de ervaring, de specifieke kennis en het juiste netwerk om de financiering van jouw droom op het water te regelen." data-id="element-756" />
          <FadeIn delay={0.2} data-id="element-757">
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-758">
              Of het nu gaat om een aankoop, verbouwing of het oversluiten van
              een bestaande woonboothypotheek: ik neem de complexiteit uit
              handen. Ik ken de specifieke eisen rondom ligplaatsvergunningen,
              kadastrale inschrijvingen en taxaties.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Waarom JDG voor woonboten? */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-759">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-760">
          <SectionHeading title="Waarom JDG voor je woonboot?" data-id="element-761" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-762">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-763">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-764">
                <NetworkIcon className="w-7 h-7 text-gold" data-id="element-765" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-766">
                Specialist Netwerk
              </h3>
              <p className="text-gray-600" data-id="element-767">
                Ik werk samen met de selecte groep banken die wél woonboten
                financieren. Ik weet precies waar jouw aanvraag de grootste kans
                van slagen heeft.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-768">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-769">
                <CheckCircleIcon className="w-7 h-7 text-gold" data-id="element-770" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-771">
                Jarenlange Ervaring
              </h3>
              <p className="text-gray-600" data-id="element-772">
                Van ligplaatsvergunningen tot scheepsregisters: ik ken de
                valkuilen en begeleid je veilig door het specifieke juridische
                en financiële proces.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-773">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-774">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-775" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-776">
                Creatieve Oplossingen
              </h3>
              <p className="text-gray-600" data-id="element-777">
                Lijkt de financiering vast te lopen? Ik denk in oplossingen en
                zoek net zolang door tot we de juiste constructie hebben
                gevonden.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Wat maakt woonbootfinanciering zo anders? */}
      <section className="py-20 md:py-28 bg-white" data-id="element-778">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-779">
          <SectionHeading title="Wat maakt woonbootfinanciering zo anders?" data-id="element-780" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-781">
            <FadeIn delay={0.1} className="bg-warm p-8 rounded-2xl border border-gray-100 relative" data-id="element-782">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-783">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-784">
                Niet elke bank doet mee
              </h3>
              <p className="text-gray-600" data-id="element-785">
                De meeste reguliere banken financieren geen woonboten. Het
                risicoprofiel is anders dan bij een stenen huis. Ik werk samen
                met een selecte groep gespecialiseerde geldverstrekkers die hier
                wél ervaring mee hebben — en ik weet precies bij welke bank jouw
                aanvraag de grootste kans van slagen heeft.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-warm p-8 rounded-2xl border border-gray-100 relative" data-id="element-786">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-787">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-788">
                Andere spelregels
              </h3>
              <p className="text-gray-600" data-id="element-789">
                Bij een woonboothypotheek gelden andere regels. De rente ligt
                vaak iets hoger door een risico-opslag, de maximale
                loan-to-value kan afwijken, en je hebt een gespecialiseerd
                taxatierapport nodig. Daarnaast moet je woonboot ingeschreven
                staan in het scheepsregister van het Kadaster en heb je een
                geldige ligplaatsvergunning nodig.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-warm p-8 rounded-2xl border border-gray-100 relative" data-id="element-790">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center text-white font-bold shadow-md mb-4" data-id="element-791">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-792">
                Verzekering is maatwerk
              </h3>
              <p className="text-gray-600" data-id="element-793">
                Een woonboot verzekeren is niet hetzelfde als een huis
                verzekeren. Je hebt een specifieke opstal- en
                inboedelverzekering nodig die rekening houdt met risico's op het
                water. Ik heb de juiste connecties om dit goed en betaalbaar te
                regelen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="bg-warm" data-id="element-794">
        <ProcessTimeline title="Het proces in 3 stappen" steps={timelineSteps} data-id="element-795" />
      </section>

      {/* Insurance Cross-sell */}
      <section className="py-16 bg-navy text-white" data-id="element-796">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-797">
          <FadeIn className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-798">
            <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-799">
              <ShieldIcon className="w-8 h-8 text-gold" data-id="element-800" />
            </div>
            <div data-id="element-801">
              <h3 className="text-2xl font-bold text-gold mb-3" data-id="element-802">
                Vergeet de woonbootverzekering niet
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6" data-id="element-803">
                Naast de financiering is ook het verzekeren van een woonboot
                maatwerk. Ik heb de juiste connecties voor een passende opstal-
                en inboedelverzekering voor jouw schip.
              </p>
              <Button variant="ghost" onClick={() => onNavigate('verzekeringen')} className="text-white hover:text-gold" data-id="element-804">
                Lees meer over onze verzekeringen
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 md:py-28 bg-white" data-id="element-805">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-806">
          <SectionHeading title="Veelgestelde Vragen" subtitle="Alles wat je moet weten over het financieren van een woonboot." data-id="element-807" />

          <FadeIn delay={0.2} className="bg-warm rounded-2xl shadow-card p-6 md:p-10 border border-gray-100" data-id="element-808">
            {faqs.map((faq, index) => <AccordionItem key={index} question={faq.question} answer={faq.answer} isOpen={openAccordion === index} onClick={() => setOpenAccordion(openAccordion === index ? null : index)} data-id="element-809" />)}
          </FadeIn>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-810">
            <p className="text-navy font-medium mb-6" data-id="element-811">
              Staat jouw vraag er niet tussen?
            </p>
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-812">
              Neem direct contact op
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <section className="py-16 bg-warm" data-id="element-813">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center" data-id="element-814">
          <FadeIn data-id="element-815">
            <p className="text-gray-500 mb-3" data-id="element-816">
              Liever een reguliere hypotheek?
            </p>
            <Button variant="ghost" onClick={() => onNavigate('hypotheken')} data-id="element-817">
              Bekijk ons hypotheekadvies
            </Button>
          </FadeIn>
        </div>
      </section>
    </div>;
}